export {
  useFlag,
  useVariant,
  useFlagsStatus,
  FlagProvider,
  useUnleashContext,
} from "@unleash/proxy-client-react";

import { useFlag, useUnleashContext } from "@unleash/proxy-client-react";
import type { IConfig } from "@unleash/proxy-client-react";
import { useEffect } from "react";

/**
 * UNLEASH FEATURE FLAGS — INFRASTRUCTURE / ROLLOUT USE ONLY
 *
 * This file mirrors the server-side Unleash flag definitions for use in the frontend.
 * Unleash is reserved exclusively for infrastructure-level concerns such as gradual
 * UI rollouts, A/B experiments, and kill switches for specific service behaviors.
 *
 * DO NOT add commercial feature entitlements here.
 * Commercial feature gating (i.e. which modules a tenant has purchased) belongs in
 * `FEATURE_CATALOG` in `shared/features.ts` and is managed via tenant entitlements.
 *
 * See: docs/developer-guide/CONTROL_PLANE_HIERARCHY.md for the full boundary definition.
 */
export const FeatureFlags = {
  // ─── ACTIVE ────────────────────────────────────────────────────────────────
  /** Phased rollout of the v2 Muster UI. Does NOT gate commercial muster access. */
  SAFETY_MUSTER_V2_UI: "rollout.safety-muster-v2",

  // ─── FUTURE (defined for discoverability; not yet deployed to Unleash) ─────
  /** Gradual rollout of redesigned operator dashboard. Add component usage first. */
  NEW_DASHBOARD: "new-dashboard",
  /** A/B rollout of Partner Portal v2 UX. Not yet implemented. */
  PARTNER_PORTAL_V2: "partner-portal-v2",
  /** Enhanced QR wallet with offline caching. Not yet implemented. */
  QR_WALLET_ENHANCED: "qr-wallet-enhanced",
  /** Infrastructure rollout for AI credential suggestions service. Not commercial. */
  AI_CREDENTIAL_SUGGESTIONS: "ai-credential-suggestions",
  /** Kill switch / rollout for the new analytics query engine. Not commercial. */
  ADVANCED_ANALYTICS_ENGINE: "advanced-analytics-engine",
  /** Emergency kill switch: disable outbound email notifications. */
  DISABLE_EMAIL_NOTIFICATIONS: "disable-email-notifications",
  /** Emergency kill switch: disable data export endpoints. */
  DISABLE_EXPORT: "disable-export",
  /** Gradual rollout to new query engine. Add implementation before enabling. */
  USE_NEW_QUERY_ENGINE: "use-new-query-engine",
  /** Infrastructure rollout for distributed caching layer. Add implementation before enabling. */
  ENABLE_CACHING_LAYER: "enable-caching-layer",
} as const;

export type FeatureFlagName = (typeof FeatureFlags)[keyof typeof FeatureFlags];

function resolveUnleashUrl(): string {
  const envUrl = (import.meta.env.VITE_UNLEASH_FRONTEND_PROXY_URL as string | undefined) || "";
  const fallbackUrl = `${window.location.origin}/api/unleash-proxy`;
  const candidate = envUrl || fallbackUrl;
  try {
    new URL(candidate);
    return candidate;
  } catch {
    return fallbackUrl;
  }
}

function resolveRefreshIntervalSeconds(): number {
  const raw = import.meta.env.VITE_UNLEASH_POLL_INTERVAL_MS as string | undefined;
  if (raw) {
    const ms = parseInt(raw, 10);
    if (!isNaN(ms) && ms > 0) {
      return Math.max(1, Math.round(ms / 1000));
    }
  }
  return 30;
}

export function buildUnleashConfig(tenantId?: string): IConfig {
  return {
    url: resolveUnleashUrl(),
    clientKey: (import.meta.env.VITE_UNLEASH_FRONTEND_TOKEN as string | undefined) || "default",
    appName: "nashid-frontend",
    refreshInterval: resolveRefreshIntervalSeconds(),
    context: tenantId ? { properties: { tenantId } } : {},
    disableMetrics: import.meta.env.DEV,
  };
}

export function useFlagForTenant(flagName: FeatureFlagName, tenantId: string | undefined): boolean {
  const updateContext = useUnleashContext();

  useEffect(() => {
    if (tenantId) {
      void updateContext({ properties: { tenantId } });
    }
  }, [tenantId, updateContext]);

  return useFlag(flagName);
}
